/**
 * Legal sections helpers and legacy mappers.
 * Schema: Array<{ id: string, title: string, content: string|Array<string>, children?: Section[] }>
 */

export function buildSection(id, title, content, children = []) {
  return { id, title, content, children };
}

/**
 * Map FAQ questions to sections schema.
 * Loads questions structure from questions.json and uses translations for text.
 * Expected translation structure:
 * - categories.{categoryId} - Category label
 * - questions.{questionId}.question - Question text
 * - questions.{questionId}.answer - Answer text
 * 
 * @param {Function} t - Translation function from useTranslation hook
 * @param {Object} questionsData - Optional questions data structure (if not provided, will try to load from questions.json)
 * @returns {Array} Array of sections for LegalLayout
 */
export function mapFAQToSections(t, questionsData = null) {
  const sections = [];
  
  // Use provided questions data or return empty sections
  // Questions data should be loaded by the component and passed in
  if (!questionsData) {
    console.warn('No questions data provided to mapFAQToSections');
    return [];
  }
  
  const questionStructure = questionsData;
  
  // Get categories and questions from translations
  const categories = t('categories', 'faq') || {};
  const questions = t('questions', 'faq') || {};
  
  // Build sections for each category
  if (questionStructure && questionStructure.categories) {
    questionStructure.categories.forEach(category => {
      const categoryLabel = categories[category.id] || category.id;
      const categoryChildren = [];
      
      // Build child sections for each question in this category
      if (category.questions && Array.isArray(category.questions)) {
        category.questions.forEach(question => {
          const questionId = question.id;
          
          // Try to get translated question/answer
          let questionText = question.question || '';
          let answerText = question.answer || '';
          
          // If translations are available, use them
          if (questions[questionId]) {
            questionText = questions[questionId].question || questionText;
            answerText = questions[questionId].answer || answerText;
          } else if (Array.isArray(questions)) {
            // Fallback: try to find in flat array structure
            const foundQuestion = questions.find(q => 
              q.id === questionId || 
              (q.question && q.question.toLowerCase().includes(questionId.replace(/-/g, ' ')))
            );
            if (foundQuestion) {
              questionText = foundQuestion.question || questionText;
              answerText = foundQuestion.answer || answerText;
            }
          }
          
          // Only add question if we have text
          if (questionText || answerText) {
            categoryChildren.push(
              buildSection(
                `question-${questionId}`,
                questionText,
                answerText
              )
            );
          }
        });
      }
      
      // Only add category if it has questions
      if (categoryChildren.length > 0) {
        sections.push(
          buildSection(
            `category-${category.id}`,
            categoryLabel,
            '', // Category sections don't have direct content
            categoryChildren
          )
        );
      }
    });
  }
  
  return sections;
}

/**
 * Map DPA translations to sections schema.
 * Expected shape (per language) - Updated for SaaS B2B structure:
 * {
 *   title, lastUpdated,
 *   contractParties: { heading, paragraph1, paragraph2 },
 *   contractIntegration: { heading, paragraph1-4 },
 *   subjectMatter: { heading, paragraph1-5, dataCategories, affectedPersons, purposes },
 *   durationAndTermination: { heading, paragraph1-3 },
 *   controllerRightsAndObligations: { heading, paragraph1-4 },
 *   processorObligations: { heading, paragraph1-6 },
 *   subprocessing: { heading, paragraph1-5, subprocessorsTable },
 *   internationalTransfers: { heading, paragraph1-4 },
 *   confidentiality: { heading, paragraph1-2 },
 *   dataSubjectRights: { heading, paragraph1-2, supportList },
 *   technicalMeasures: { heading, paragraph1-5, confidentialityList, integrityList, availabilityList },
 *   controlRights: { heading, paragraph1-3, inspectionList },
 *   termination: { heading, paragraph1-3 },
 *   liability: { heading, paragraph1-2 },
 *   finalProvisions: { heading, paragraph1-4 },
 *   annex: { heading, title, paragraph1, confidentiality, dataIntegrity, availability, evaluation }
 * }
 */
export function mapLegacyDpaToSections(t) {
  const sections = [];

  // Contract Parties (new structure)
  const contractParties = t('contractParties', 'dpa');
  if (contractParties?.heading) {
    const content = [
      contractParties.paragraph1 || '',
      contractParties.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('contract-parties', contractParties.heading, content)
    );
  }

  // Section 1: Contract Integration (replaces "General")
  const contractIntegration = t('contractIntegration', 'dpa');
  if (contractIntegration?.heading) {
    const content = [
      contractIntegration.paragraph1 || '',
      contractIntegration.paragraph2 || '',
      contractIntegration.paragraph3 || '',
      contractIntegration.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('contract-integration', contractIntegration.heading, content)
    );
  }

  // Section 3: Duration and Termination
  const duration = t('durationAndTermination', 'dpa');
  if (duration?.heading) {
    const content = [
      duration.paragraph1 || '',
      duration.paragraph2 || '',
      duration.paragraph3 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('duration-termination', duration.heading, content)
    );
  }

  // Section 2: Subject Matter of Processing
  const subject = t('subjectMatter', 'dpa');
  if (subject?.heading) {
    const contentParts = [subject.paragraph1 || ''];
    
    if (subject.paragraph2) {
      contentParts.push(`\n\n${subject.paragraph2}`);
    }
    if (subject.dataCategories && Array.isArray(subject.dataCategories)) {
      contentParts.push(subject.dataCategories.map(item => `- ${item}`).join('\n'));
    }
    
    if (subject.paragraph3) {
      contentParts.push(`\n\n${subject.paragraph3}`);
    }
    if (subject.affectedPersons && Array.isArray(subject.affectedPersons)) {
      contentParts.push(subject.affectedPersons.map(item => `- ${item}`).join('\n'));
    }
    
    if (subject.paragraph4) {
      contentParts.push(`\n\n${subject.paragraph4}`);
    }
    if (subject.purposes && Array.isArray(subject.purposes)) {
      contentParts.push(subject.purposes.map(item => `- ${item}`).join('\n'));
    }
    
    if (subject.paragraph5) {
      contentParts.push(`\n\n${subject.paragraph5}`);
    }
    
    sections.push(
      buildSection('subject-matter', subject.heading, contentParts.filter(Boolean).join('\n'))
    );
  }

  // Section 4: Rights and Obligations of the Controller
  const controller = t('controllerRightsAndObligations', 'dpa');
  if (controller?.heading) {
    const content = [
      controller.paragraph1 || '',
      controller.paragraph2 || '',
      controller.paragraph3 || '',
      controller.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('controller-rights', controller.heading, content)
    );
  }

  // Section 5: General Obligations of the Processor
  const processor = t('processorObligations', 'dpa');
  if (processor?.heading) {
    const content = [
      processor.paragraph1 || '',
      processor.paragraph2 || '',
      processor.paragraph3 || '',
      processor.paragraph4 || '',
      processor.paragraph5 || '',
      processor.paragraph6 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('processor-obligations', processor.heading, content)
    );
  }

  // Section 11: Control Rights of the Controller
  const control = t('controlRights', 'dpa');
  if (control?.heading) {
    const contentParts = [
      control.paragraph1 || '',
      control.paragraph2 || ''
    ];
    
    if (control.inspectionList && Array.isArray(control.inspectionList)) {
      contentParts.push(control.inspectionList.map(item => `- ${item}`).join('\n'));
    }
    
    if (control.paragraph3) {
      contentParts.push(`\n\n${control.paragraph3}`);
    }
    
    sections.push(
      buildSection('control-rights', control.heading, contentParts.filter(Boolean).join('\n\n'))
    );
  }

  // Section 6: Sub-processors
  const subprocessing = t('subprocessing', 'dpa');
  if (subprocessing?.heading) {
    const contentParts = [subprocessing.paragraph1 || ''];
    
    // Add table if present
    if (subprocessing.subprocessorsTable) {
      contentParts.push('\n\n' + subprocessing.subprocessorsTable);
    }
    
    contentParts.push(
      subprocessing.paragraph2 || '',
      subprocessing.paragraph3 || '',
      subprocessing.paragraph4 || '',
      subprocessing.paragraph5 || ''
    );
    
    sections.push(
      buildSection('subprocessing', subprocessing.heading, contentParts.filter(Boolean).join('\n\n'))
    );
  }

  // Section 7: International Data Transfers (new section)
  const internationalTransfers = t('internationalTransfers', 'dpa');
  if (internationalTransfers?.heading) {
    const content = [
      internationalTransfers.paragraph1 || '',
      internationalTransfers.paragraph2 || '',
      internationalTransfers.paragraph3 || '',
      internationalTransfers.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('international-transfers', internationalTransfers.heading, content)
    );
  }

  // Section 8: Confidentiality and Secrecy
  const confidentiality = t('confidentiality', 'dpa');
  if (confidentiality?.heading) {
    const content = [
      confidentiality.paragraph1 || '',
      confidentiality.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('confidentiality', confidentiality.heading, content)
    );
  }

  // Section 9: Safeguarding Data Subject Rights
  const dataSubjectRights = t('dataSubjectRights', 'dpa');
  if (dataSubjectRights?.heading) {
    const contentParts = [
      dataSubjectRights.paragraph1 || '',
      dataSubjectRights.paragraph2 || ''
    ];
    
    if (dataSubjectRights.supportList && Array.isArray(dataSubjectRights.supportList)) {
      contentParts.push(dataSubjectRights.supportList.map(item => `- ${item}`).join('\n'));
    }
    
    sections.push(
      buildSection('data-subject-rights', dataSubjectRights.heading, contentParts.filter(Boolean).join('\n\n'))
    );
  }

  // Section 12: Termination and Data Return
  const termination = t('termination', 'dpa');
  if (termination?.heading) {
    const content = [
      termination.paragraph1 || '',
      termination.paragraph2 || '',
      termination.paragraph3 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('termination', termination.heading, content)
    );
  }

  // Section 13: Liability (new section)
  const liability = t('liability', 'dpa');
  if (liability?.heading) {
    const content = [
      liability.paragraph1 || '',
      liability.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('liability', liability.heading, content)
    );
  }

  // Section 14: Final Provisions
  const final = t('finalProvisions', 'dpa');
  if (final?.heading) {
    const content = [
      final.paragraph1 || '',
      final.paragraph2 || '',
      final.paragraph3 || '',
      final.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('final-provisions', final.heading, content)
    );
  }

  return sections;
}

/**
 * Map Business DPA translations to sections schema.
 * Similar to mapLegacyDpaToSections but for cost-pool-level Business DPA.
 */
export function mapBusinessDpaToSections(t) {
  const sections = [];

  // Contract Parties Section
  const contractPartiesSection = t('contractPartiesSection', 'businessdpa');
  if (contractPartiesSection?.heading) {
    const content = [
      contractPartiesSection.paragraph1 || '',
      contractPartiesSection.paragraph2 || '',
      contractPartiesSection.list2a || '',
      contractPartiesSection.list2b || '',
      contractPartiesSection.list2c || '',
      contractPartiesSection.paragraph3 || '',
      contractPartiesSection.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('contract-parties-section', contractPartiesSection.heading, content)
    );
  }

  // Subject Matter of Processing
  const subject = t('subjectMatter', 'businessdpa');
  if (subject?.heading) {
    const contentParts = [
      `**${subject.processingActivities}**`,
      subject.activitiesList ? subject.activitiesList.map(item => `- ${item}`).join('\n') : '',
      `\n\n**${subject.dataCategories}**`,
      subject.dataCategoriesList ? subject.dataCategoriesList.map(item => `- ${item}`).join('\n') : '',
      `\n\n**${subject.dataSubjects}**`,
      subject.dataSubjectsList ? subject.dataSubjectsList.map(item => `- ${item}`).join('\n') : '',
      `\n\n**${subject.purposes}**`,
      subject.purposesList ? subject.purposesList.map(item => `- ${item}`).join('\n') : '',
      `\n\n**${subject.duration}**`,
      subject.durationText || ''
    ].filter(Boolean).join('\n');
    sections.push(
      buildSection('subject-matter', subject.heading, contentParts)
    );
  }

  // Subprocessors
  const subprocessors = t('subprocessors', 'businessdpa');
  if (subprocessors?.heading) {
    const contentParts = [subprocessors.paragraph1 || ''];
    
    // Add table if present
    if (subprocessors.subprocessorsTable) {
      contentParts.push('\n\n' + subprocessors.subprocessorsTable);
    }
    
    contentParts.push(
      subprocessors.paragraph2 || '',
      subprocessors.paragraph3 || '',
      subprocessors.paragraph4 || '',
      subprocessors.paragraph5 || ''
    );
    
    sections.push(
      buildSection('subprocessors', subprocessors.heading, contentParts.filter(Boolean).join('\n\n'))
    );
  }

  // Billing Providers (Stripe)
  const billingProviders = t('billingProviders', 'businessdpa');
  if (billingProviders?.heading) {
    const content = [
      billingProviders.paragraph1 || '',
      billingProviders.paragraph2 || '',
      billingProviders.paragraph3 || '',
      billingProviders.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('billing-providers', billingProviders.heading, content)
    );
  }

  // Technical and Organizational Measures
  const technical = t('technicalAndOrganizationalMeasures', 'businessdpa');
  if (technical?.heading) {
    const content = technical.paragraph || '';
    sections.push(
      buildSection('technical-measures', technical.heading, content)
    );
  }

  // International Data Transfers
  const internationalTransfers = t('internationalDataTransfers', 'businessdpa');
  if (internationalTransfers?.heading) {
    const content = [
      internationalTransfers.paragraph1 || '',
      internationalTransfers.paragraph2 || '',
      internationalTransfers.paragraph3 || '',
      internationalTransfers.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('international-transfers', internationalTransfers.heading, content)
    );
  }

  // Termination and Data Deletion
  const termination = t('terminationAndDataDeletion', 'businessdpa');
  if (termination?.heading) {
    const content = [
      termination.paragraph1 || '',
      termination.paragraph2 || '',
      termination.paragraph3 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('termination', termination.heading, content)
    );
  }

  // Liability
  const liability = t('liability', 'businessdpa');
  if (liability?.heading) {
    const content = [
      liability.paragraph1 || '',
      liability.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('liability', liability.heading, content)
    );
  }

  // Final Provisions
  const final = t('finalProvisions', 'businessdpa');
  if (final?.heading) {
    const content = [
      final.paragraph1 || '',
      final.paragraph2 || '',
      final.paragraph3 || '',
      final.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(
      buildSection('final-provisions', final.heading, content)
    );
  }

  return sections;
}

/**
 * Map Impressum translations to sections schema.
 * Expected shape (per language):
 * {
 *   title, lastUpdated,
 *   responsibleParty: { heading, company, address, city, country },
 *   contact: { heading, phone, email },
 *   authorizedRepresentatives: { heading, name },
 *   registration: { heading, registeredOffice, registerCourt, registrationNumber },
 *   vatId: { heading, paragraph, vatNumber },
 *   contentResponsibility: { heading, name, address, city },
 *   disclaimer: { heading, contentLiability: {...}, linkLiability: {...}, copyright: {...} },
 *   professionalLiabilityInsurance: { heading, insuranceCompany, coverageArea, insuranceAmount },
 *   disputeResolution: { heading, paragraph1, link, paragraph2 },
 *   legalNotes: { heading, applicableLaw: {...}, jurisdiction: {...}, severabilityClause: {...} }
 * }
 */
export function mapImpressumToSections(t) {
  const sections = [];

  // Responsible Party
  const resp = t('responsibleParty', 'impressum');
  if (resp?.heading) {
    const content = [
      `**${resp.company || ''}**`,
      resp.address || '',
      resp.city || '',
      resp.country || ''
    ].filter(Boolean).join('\n');
    sections.push(buildSection('responsible-party', resp.heading, content));
  }

  // Contact
  const contact = t('contact', 'impressum');
  if (contact?.heading) {
    const content = [
      contact.phone || '',
      contact.email || ''
    ].filter(Boolean).join('\n');
    sections.push(buildSection('contact', contact.heading, content));
  }

  // Authorized Representatives
  const authRep = t('authorizedRepresentatives', 'impressum');
  if (authRep?.heading) {
    sections.push(buildSection('authorized-representatives', authRep.heading, authRep.name || ''));
  }

  // Registration
  const reg = t('registration', 'impressum');
  const vat = t('vatId', 'impressum');
  if (reg?.heading) {
    const content = [
      reg.registeredOffice || '',
      reg.registerCourt || '',
      reg.registrationNumber || '',
      vat?.paragraph ? `${vat.paragraph} ${vat.vatNumber || ''}` : ''
    ].filter(Boolean).join('\n');
    sections.push(buildSection('registration', reg.heading, content));
  }

  // Content Responsibility
  const contentResp = t('contentResponsibility', 'impressum');
  if (contentResp?.heading) {
    const content = [
      contentResp.name || '',
      contentResp.address || '',
      contentResp.city || ''
    ].filter(Boolean).join('\n');
    sections.push(buildSection('content-responsibility', contentResp.heading, content));
  }

  // Disclaimer (with children)
  const disclaimer = t('disclaimer', 'impressum');
  if (disclaimer?.heading) {
    const children = [];
    if (disclaimer.contentLiability?.heading) {
      children.push(buildSection('content-liability', disclaimer.contentLiability.heading, disclaimer.contentLiability.paragraph || ''));
    }
    if (disclaimer.linkLiability?.heading) {
      children.push(buildSection('link-liability', disclaimer.linkLiability.heading, disclaimer.linkLiability.paragraph || ''));
    }
    if (disclaimer.copyright?.heading) {
      children.push(buildSection('copyright', disclaimer.copyright.heading, disclaimer.copyright.paragraph || ''));
    }
    sections.push(buildSection('disclaimer', disclaimer.heading, '', children));
  }

  // Professional Liability Insurance
  const insurance = t('professionalLiabilityInsurance', 'impressum');
  if (insurance?.heading) {
    const content = [
      insurance.insuranceCompany || '',
      insurance.coverageArea || '',
      insurance.insuranceAmount || ''
    ].filter(Boolean).join('\n');
    sections.push(buildSection('professional-liability-insurance', insurance.heading, content));
  }

  // Dispute Resolution
  const dispute = t('disputeResolution', 'impressum');
  if (dispute?.heading) {
    const content = [
      dispute.paragraph1 || '',
      dispute.link ? `[${dispute.link}](${dispute.link})` : '',
      dispute.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('dispute-resolution', dispute.heading, content));
  }

  // Legal Notes (with children)
  const legalNotes = t('legalNotes', 'impressum');
  if (legalNotes?.heading) {
    const children = [];
    if (legalNotes.applicableLaw?.heading) {
      children.push(buildSection('applicable-law', legalNotes.applicableLaw.heading, legalNotes.applicableLaw.paragraph || ''));
    }
    if (legalNotes.jurisdiction?.heading) {
      children.push(buildSection('jurisdiction', legalNotes.jurisdiction.heading, legalNotes.jurisdiction.paragraph || ''));
    }
    if (legalNotes.severabilityClause?.heading) {
      children.push(buildSection('severability-clause', legalNotes.severabilityClause.heading, legalNotes.severabilityClause.paragraph || ''));
    }
    sections.push(buildSection('legal-notes', legalNotes.heading, '', children));
  }

  return sections;
}

/**
 * Map Acceptable Use Policy translations to sections schema.
 */
export function mapAcceptableUseToSections(t) {
  const sections = [];

  const intro = t('introduction', 'acceptableuse');
  if (intro?.heading) {
    const content = [
      intro.paragraph1 || '',
      intro.paragraph2 || '',
      intro.list ? intro.list.map(item => `- ${item}`).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('introduction', intro.heading, content));
  }

  const whoWeAre = t('whoWeAre', 'acceptableuse');
  if (whoWeAre?.heading) {
    sections.push(buildSection('who-we-are', whoWeAre.heading, whoWeAre.paragraph || ''));
  }

  const useOfServices = t('useOfServices', 'acceptableuse');
  if (useOfServices?.heading) {
    const content = [
      useOfServices.paragraph1 || '',
      useOfServices.paragraph2 || '',
      useOfServices.list ? useOfServices.list.map(item => `- ${item}`).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('use-of-services', useOfServices.heading, content));
  }

  const subscriptions = t('subscriptions', 'acceptableuse');
  if (subscriptions?.heading) {
    const content = [
      subscriptions.paragraph1 || '',
      subscriptions.list ? subscriptions.list.map(item => `- ${item}`).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('subscriptions', subscriptions.heading, content));
  }

  const aiProducts = t('aiProducts', 'acceptableuse');
  if (aiProducts?.heading) {
    const content = [
      aiProducts.paragraph1 || '',
      aiProducts.list ? aiProducts.list.map(item => `- ${item}`).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('ai-products', aiProducts.heading, content));
  }

  const ai = t('artificialIntelligence', 'acceptableuse');
  if (ai?.heading) {
    const content = [
      ai.paragraph1 || '',
      ai.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('artificial-intelligence', ai.heading, content));
  }

  const enforcement = t('enforcement', 'acceptableuse');
  if (enforcement?.heading) {
    const content = [
      enforcement.paragraph1 || '',
      enforcement.paragraph2 || '',
      enforcement.list ? enforcement.list.map(item => `- ${item}`).join('\n') : '',
      enforcement.paragraph3 || '',
      enforcement.actions ? enforcement.actions.map(item => `- ${item}`).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('enforcement', enforcement.heading, content));
  }

  const responsibleAi = t('responsibleAi', 'acceptableuse');
  if (responsibleAi?.heading) {
    const content = [
      responsibleAi.paragraph1 || '',
      responsibleAi.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('responsible-ai', responsibleAi.heading, content));
  }

  const consequences = t('consequences', 'acceptableuse');
  if (consequences?.heading) {
    const content = [
      consequences.paragraph1 || '',
      consequences.paragraph2 || '',
      consequences.paragraph3 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('consequences', consequences.heading, content));
  }

  const contact = t('contact', 'acceptableuse');
  if (contact?.heading) {
    const content = [
      contact.paragraph || '',
      contact.email || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('contact', contact.heading, content));
  }

  return sections;
}

/**
 * Map EULA translations to sections schema.
 */
export function mapEulaToSections(t) {
  const sections = [];

  const intro = t('introduction', 'eula');
  if (intro?.heading) {
    const content = [
      intro.paragraph1 || '',
      intro.paragraph2 || '',
      intro.paragraph3 || '',
      intro.paragraph4 || '',
      intro.paragraph5 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('introduction', intro.heading, content));
  }

  const toc = t('tableOfContents', 'eula');
  if (toc?.heading && toc?.items) {
    const content = toc.items.map((item, idx) => `${idx + 1}. ${item}`).join('\n');
    sections.push(buildSection('table-of-contents', toc.heading, content));
  }

  const app = t('theApplication', 'eula');
  if (app?.heading) {
    sections.push(buildSection('the-application', app.heading, app.paragraph || ''));
  }

  const scope = t('scopeOfLicense', 'eula');
  if (scope?.heading) {
    sections.push(buildSection('scope-of-license', scope.heading, scope.paragraph || ''));
  }

  const tech = t('technicalRequirements', 'eula');
  if (tech?.heading) {
    sections.push(buildSection('technical-requirements', tech.heading, tech.paragraph || ''));
  }

  const maintenance = t('noMaintenanceAndSupport', 'eula');
  if (maintenance?.heading) {
    const content = [
      maintenance.paragraph1 || '',
      maintenance.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('no-maintenance-support', maintenance.heading, content));
  }

  const contributions = t('userGeneratedContributions', 'eula');
  if (contributions?.heading) {
    const content = [
      contributions.paragraph || '',
      contributions.list ? contributions.list.map(item => `- ${item}`).join('\n') : '',
      contributions.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('user-contributions', contributions.heading, content));
  }

  const contribLicense = t('contributionLicense', 'eula');
  if (contribLicense?.heading) {
    const content = [
      contribLicense.paragraph1 || '',
      contribLicense.paragraph2 || '',
      contribLicense.paragraph3 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('contribution-license', contribLicense.heading, content));
  }

  const liability = t('liability', 'eula');
  if (liability?.heading) {
    sections.push(buildSection('liability', liability.heading, liability.paragraph || ''));
  }

  const warranty = t('warranty', 'eula');
  if (warranty?.heading) {
    const content = [
      warranty.paragraph1 || '',
      warranty.paragraph2 || '',
      warranty.paragraph3 || '',
      warranty.paragraph4 || '',
      warranty.paragraph5 || '',
      warranty.paragraph6 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('warranty', warranty.heading, content));
  }

  const claims = t('productClaims', 'eula');
  if (claims?.heading) {
    const content = [
      claims.paragraph || '',
      claims.list ? claims.list.map(item => item).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('product-claims', claims.heading, content));
  }

  const compliance = t('legalCompliance', 'eula');
  if (compliance?.heading) {
    sections.push(buildSection('legal-compliance', compliance.heading, compliance.paragraph || ''));
  }

  const contact = t('contactInformation', 'eula');
  if (contact?.heading) {
    const content = [
      contact.paragraph || '',
      contact.details ? contact.details.join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('contact-information', contact.heading, content));
  }

  const termination = t('termination', 'eula');
  if (termination?.heading) {
    sections.push(buildSection('termination', termination.heading, termination.paragraph || ''));
  }

  const thirdParty = t('thirdPartyTerms', 'eula');
  if (thirdParty?.heading) {
    const content = [
      thirdParty.paragraph1 || '',
      thirdParty.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('third-party-terms', thirdParty.heading, content));
  }

  const ip = t('intellectualPropertyRights', 'eula');
  if (ip?.heading) {
    sections.push(buildSection('intellectual-property', ip.heading, ip.paragraph || ''));
  }

  const law = t('applicableLaw', 'eula');
  if (law?.heading) {
    sections.push(buildSection('applicable-law', law.heading, law.paragraph || ''));
  }

  const misc = t('miscellaneous', 'eula');
  if (misc?.heading) {
    const content = [
      misc.paragraph1 || '',
      misc.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('miscellaneous', misc.heading, content));
  }

  return sections;
}

/**
 * Map Disclaimer translations to sections schema.
 */
export function mapDisclaimerToSections(t) {
  const sections = [];

  const website = t('websiteDisclaimer', 'disclaimer');
  if (website?.heading) {
    const content = [
      website.paragraph1 || '',
      website.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('website-disclaimer', website.heading, content));
  }

  const testimonials = t('testimonialsDisclaimer', 'disclaimer');
  if (testimonials?.heading) {
    const content = [
      testimonials.paragraph1 || '',
      testimonials.paragraph2 || '',
      testimonials.paragraph3 || '',
      testimonials.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('testimonials-disclaimer', testimonials.heading, content));
  }

  return sections;
}

/**
 * Map Privacy Policy translations to sections schema.
 */
export function mapPrivacyPolicyToSections(t) {
  const sections = [];

  const intro = t('introduction', 'privacy');
  if (intro?.heading) {
    const content = [
      intro.generalNotes?.paragraph || '',
      intro.dataCollection?.paragraph1 || '',
      intro.dataCollection?.paragraph2 || '',
      intro.dataCollection?.paragraph3 || '',
      intro.dataCollection?.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('introduction', intro.heading, content));
  }

  const summary = t('summaryOfKeyPoints', 'privacy');
  if (summary?.heading) {
    const content = [
      summary.paragraph1 || '',
      summary.whatPersonalInformationDoWeProcess ? `**${summary.whatPersonalInformationDoWeProcess.question}** ${summary.whatPersonalInformationDoWeProcess.answer}` : '',
      summary.doWeProcessAnySensitivePersonalInformation ? `**${summary.doWeProcessAnySensitivePersonalInformation.question}** ${summary.doWeProcessAnySensitivePersonalInformation.answer}` : '',
      summary.doWeCollectAnyInformationFromThirdParties ? `**${summary.doWeCollectAnyInformationFromThirdParties.question}** ${summary.doWeCollectAnyInformationFromThirdParties.answer}` : '',
      summary.howDoWeProcessYourInformation ? `**${summary.howDoWeProcessYourInformation.question}** ${summary.howDoWeProcessYourInformation.answer}` : '',
      summary.inWhatSituationsAndWithWhichPartiesDoWeSharePersonalInformation ? `**${summary.inWhatSituationsAndWithWhichPartiesDoWeSharePersonalInformation.question}** ${summary.inWhatSituationsAndWithWhichPartiesDoWeSharePersonalInformation.answer}` : '',
      summary.howDoWeKeepYourInformationSafe ? `**${summary.howDoWeKeepYourInformationSafe.question}** ${summary.howDoWeKeepYourInformationSafe.answer}` : '',
      summary.whatAreYourRights ? `**${summary.whatAreYourRights.question}** ${summary.whatAreYourRights.answer}` : '',
      summary.howDoYouExerciseYourRights ? `**${summary.howDoYouExerciseYourRights.question}** ${summary.howDoYouExerciseYourRights.answer}` : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('summary', summary.heading, content));
  }

  const whatInfo = t('whatInformationDoWeCollect', 'privacy');
  if (whatInfo?.heading) {
    const personalInfo = whatInfo.personalInformationYouDiscloseToUs;
    const autoInfo = whatInfo.informationAutomaticallyCollected;
    const content = [
      personalInfo?.inShort ? `**${personalInfo.inShort}** ${personalInfo.paragraph1 || ''}` : '',
      personalInfo?.paragraph2 || '',
      personalInfo?.personalInformationProvidedByYou?.heading || '',
      personalInfo?.personalInformationProvidedByYou?.paragraph || '',
      autoInfo?.inShort ? `**${autoInfo.inShort}** ${autoInfo.paragraph1 || ''}` : '',
      autoInfo?.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('what-information', whatInfo.heading, content));
  }

  const howProcess = t('howDoWeProcessYourInformation', 'privacy');
  if (howProcess?.heading) {
    const content = [
      howProcess.inShort ? `**${howProcess.inShort}** ${howProcess.paragraph1 || ''}` : '',
      howProcess.paragraph2 || '',
      howProcess.list ? Object.values(howProcess.list).map(item => typeof item === 'string' ? `- ${item}` : `- ${item.heading || ''} ${item.paragraph || ''}`).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('how-process', howProcess.heading, content));
  }

  const legalBases = t('whatLegalBasesDoWeRelyOnToProcessYourInformation', 'privacy');
  if (legalBases?.heading) {
    const content = [
      legalBases.inShort ? `**${legalBases.inShort}** ${legalBases.paragraph1 || ''}` : '',
      legalBases.paragraph2 || '',
      legalBases.list ? Object.values(legalBases.list).map(item => `- **${item.heading || ''}** ${item.paragraph || ''}`).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('legal-bases', legalBases.heading, content));
  }

  const whenShare = t('whenAndWithWhomDoWeShareYourPersonalInformation', 'privacy');
  if (whenShare?.heading) {
    const content = [
      whenShare.inShort ? `**${whenShare.inShort}** ${whenShare.paragraph1 || ''}` : '',
      whenShare.paragraph2 || '',
      whenShare.list ? Object.values(whenShare.list).map(item => `- **${item.heading || ''}** ${item.paragraph || ''}`).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('when-share', whenShare.heading, content));
  }

  const cookies = t('doWeUseCookiesAndOtherTrackingTechnologies', 'privacy');
  if (cookies?.heading) {
    const content = [
      cookies.inShort ? `**${cookies.inShort}** ${cookies.paragraph1 || ''}` : '',
      cookies.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('cookies', cookies.heading, content));
  }

  const aiProducts = t('doWeOfferArtificialIntelligenceBasedProducts', 'privacy');
  if (aiProducts?.heading) {
    const content = [
      aiProducts.inShort ? `**${aiProducts.inShort}** ${aiProducts.paragraph1 || ''}` : '',
      aiProducts.howWeProcessYourDataUsingAi ? `**${aiProducts.howWeProcessYourDataUsingAi.heading}** ${aiProducts.howWeProcessYourDataUsingAi.paragraph || ''}` : '',
      aiProducts.useOfAiTechnologies ? `**${aiProducts.useOfAiTechnologies.heading}** ${aiProducts.useOfAiTechnologies.paragraph || ''}` : '',
      aiProducts.ourAiProducts ? Object.values(aiProducts.ourAiProducts).map(item => `- **${item.heading || ''}** ${item.paragraph || ''}`).join('\n') : '',
      aiProducts.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('ai-products', aiProducts.heading, content));
  }

  return sections;
}

/**
 * Map Terms of Service translations to sections schema.
 * Handles all 27 sections from 001_termsandconditions.txt
 */
export function mapTermsOfServiceToSections(t) {
  const sections = [];

  // 1. Agreement to Terms
  const agreement = t('agreementToTerms', 'terms');
  if (agreement?.heading) {
    const content = [
      agreement.paragraph1 || '',
      agreement.paragraph2 || '',
      agreement.paragraph3 || '',
      agreement.paragraph4 || '',
      agreement.paragraph5 || '',
      agreement.paragraph6 || '',
      agreement.paragraph7 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('agreement', agreement.heading, content));
  }

  // 2. Table of Contents
  const toc = t('tableOfContents', 'terms');
  if (toc?.heading && toc?.items) {
    const content = toc.items.map((item, idx) => `${idx + 1}. ${item}`).join('\n');
    sections.push(buildSection('table-of-contents', toc.heading, content));
  }

  // 3. Our Services
  const services = t('ourServices', 'terms');
  if (services?.heading) {
    sections.push(buildSection('our-services', services.heading, services.paragraph1 || ''));
  }

  // 4. Intellectual Property Rights
  const ip = t('intellectualProperty', 'terms');
  if (ip?.heading) {
    const children = [];
    
    if (ip.ourIntellectualProperty?.heading) {
      const ourIpContent = [
        ip.ourIntellectualProperty.paragraph1 || '',
        ip.ourIntellectualProperty.paragraph2 || '',
        ip.ourIntellectualProperty.paragraph3 || ''
      ].filter(Boolean).join('\n\n');
      children.push(buildSection('our-ip', ip.ourIntellectualProperty.heading, ourIpContent));
    }
    
    if (ip.yourUseOfServices?.heading) {
      const yourUseContent = [
        ip.yourUseOfServices.paragraph1 || '',
        ip.yourUseOfServices.list ? ip.yourUseOfServices.list.map(item => `- ${item}`).join('\n') : '',
        ip.yourUseOfServices.paragraph2 || '',
        ip.yourUseOfServices.paragraph3 || '',
        ip.yourUseOfServices.paragraph4 || '',
        ip.yourUseOfServices.paragraph5 || ''
      ].filter(Boolean).join('\n\n');
      children.push(buildSection('your-use', ip.yourUseOfServices.heading, yourUseContent));
    }
    
    if (ip.yourSubmissions?.heading) {
      const submissionsContent = [
        ip.yourSubmissions.paragraph1 || '',
        ip.yourSubmissions.paragraph2 || '',
        ip.yourSubmissions.paragraph3 || '',
        ip.yourSubmissions.list ? ip.yourSubmissions.list.map(item => `- ${item}`).join('\n') : '',
        ip.yourSubmissions.paragraph4 || ''
      ].filter(Boolean).join('\n\n');
      children.push(buildSection('your-submissions', ip.yourSubmissions.heading, submissionsContent));
    }
    
    sections.push(buildSection('intellectual-property', ip.heading, '', children));
  }

  // 5. User Representations
  const userRep = t('userRepresentations', 'terms');
  if (userRep?.heading) {
    const content = [
      userRep.paragraph1 || '',
      userRep.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('user-representations', userRep.heading, content));
  }

  // 6. User Registration
  const userReg = t('userRegistration', 'terms');
  if (userReg?.heading) {
    sections.push(buildSection('user-registration', userReg.heading, userReg.paragraph || ''));
  }

  // 7. Purchases and Payment
  const purchases = t('purchasesAndPayment', 'terms');
  if (purchases?.heading) {
    const content = [
      purchases.paragraph1 || '',
      purchases.paymentMethods ? purchases.paymentMethods.map(m => `- ${m}`).join('\n') : '',
      purchases.paragraph2 || '',
      purchases.paragraph3 || '',
      purchases.paragraph4 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('purchases-payment', purchases.heading, content));
  }

  // 8. Subscriptions
  const subs = t('subscriptions', 'terms');
  if (subs?.heading) {
    const children = [];
    if (subs.billingAndRenewal?.heading) {
      children.push(buildSection('billing-renewal', subs.billingAndRenewal.heading, subs.billingAndRenewal.paragraph || ''));
    }
    if (subs.freeTrial?.heading) {
      children.push(buildSection('free-trial', subs.freeTrial.heading, subs.freeTrial.paragraph || ''));
    }
    if (subs.cancellation?.heading) {
      children.push(buildSection('cancellation', subs.cancellation.heading, subs.cancellation.paragraph || ''));
    }
    if (subs.feeChanges?.heading) {
      children.push(buildSection('fee-changes', subs.feeChanges.heading, subs.feeChanges.paragraph || ''));
    }
    sections.push(buildSection('subscriptions', subs.heading, '', children));
  }

  // 9. Prohibited Activities
  const prohibited = t('prohibitedActivities', 'terms');
  if (prohibited?.heading) {
    const content = [
      prohibited.paragraph1 || '',
      prohibited.paragraph2 || '',
      prohibited.list ? prohibited.list.map(item => `- ${item}`).join('\n') : ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('prohibited-activities', prohibited.heading, content));
  }

  // 10. User Generated Contributions
  const contributions = t('userGeneratedContributions', 'terms');
  if (contributions?.heading) {
    const content = [
      contributions.paragraph1 || '',
      contributions.list ? contributions.list.map(item => `- ${item}`).join('\n') : '',
      contributions.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('user-contributions', contributions.heading, content));
  }

  // 11. Contribution License
  const contribLicense = t('contributionLicense', 'terms');
  if (contribLicense?.heading) {
    const content = [
      contribLicense.paragraph1 || '',
      contribLicense.paragraph2 || '',
      contribLicense.paragraph3 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('contribution-license', contribLicense.heading, content));
  }

  // 12. Third-Party Websites and Content
  const thirdParty = t('thirdPartyWebsitesAndContent', 'terms');
  if (thirdParty?.heading) {
    sections.push(buildSection('third-party', thirdParty.heading, thirdParty.paragraph || ''));
  }

  // 13. Services Management
  const servicesMgmt = t('servicesManagement', 'terms');
  if (servicesMgmt?.heading) {
    sections.push(buildSection('services-management', servicesMgmt.heading, servicesMgmt.paragraph || ''));
  }

  // 14. Privacy Policy
  const privacy = t('privacyPolicy', 'terms');
  if (privacy?.heading) {
    sections.push(buildSection('privacy-policy', privacy.heading, privacy.paragraph || ''));
  }

  // 15. Term and Termination
  const termTerm = t('termAndTermination', 'terms');
  if (termTerm?.heading) {
    const content = [
      termTerm.paragraph1 || '',
      termTerm.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('term-termination', termTerm.heading, content));
  }

  // 16. Modifications and Interruptions
  const mods = t('modificationsAndInterruptions', 'terms');
  if (mods?.heading) {
    const content = [
      mods.paragraph1 || '',
      mods.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('modifications', mods.heading, content));
  }

  // 17. Governing Law
  const law = t('governingLaw', 'terms');
  if (law?.heading) {
    sections.push(buildSection('governing-law', law.heading, law.paragraph || ''));
  }

  // 18. Dispute Resolution
  const dispute = t('disputeResolution', 'terms');
  if (dispute?.heading) {
    sections.push(buildSection('dispute-resolution', dispute.heading, dispute.paragraph || ''));
  }

  // 19. Corrections
  const corrections = t('corrections', 'terms');
  if (corrections?.heading) {
    sections.push(buildSection('corrections', corrections.heading, corrections.paragraph || ''));
  }

  // 20. Disclaimer
  const disclaimer = t('disclaimer', 'terms');
  if (disclaimer?.heading) {
    sections.push(buildSection('disclaimer', disclaimer.heading, disclaimer.paragraph || ''));
  }

  // 21. Limitations of Liability
  const liability = t('limitationsOfLiability', 'terms');
  if (liability?.heading) {
    sections.push(buildSection('limitations-liability', liability.heading, liability.paragraph || ''));
  }

  // 22. Indemnification
  const indemn = t('indemnification', 'terms');
  if (indemn?.heading) {
    sections.push(buildSection('indemnification', indemn.heading, indemn.paragraph || ''));
  }

  // 23. User Data
  const userData = t('userData', 'terms');
  if (userData?.heading) {
    sections.push(buildSection('user-data', userData.heading, userData.paragraph || ''));
  }

  // 24. Electronic Communications
  const electronic = t('electronicCommunications', 'terms');
  if (electronic?.heading) {
    sections.push(buildSection('electronic-communications', electronic.heading, electronic.paragraph || ''));
  }

  // 25. Miscellaneous
  const misc = t('miscellaneous', 'terms');
  if (misc?.heading) {
    sections.push(buildSection('miscellaneous', misc.heading, misc.paragraph || ''));
  }

  // 26. Scope of Services
  const scope = t('scopeOfServices', 'terms');
  if (scope?.heading) {
    sections.push(buildSection('scope-services', scope.heading, scope.paragraph || ''));
  }

  // 27. Usage and Service Limitations
  const usage = t('usageAndServiceLimitations', 'terms');
  if (usage?.heading) {
    sections.push(buildSection('usage-limitations', usage.heading, usage.paragraph || ''));
  }

  // 28. Service Availability and Updates
  const availability = t('serviceAvailabilityAndUpdates', 'terms');
  if (availability?.heading) {
    sections.push(buildSection('service-availability', availability.heading, availability.paragraph || ''));
  }

  // 29. Contact Us
  const contact = t('contactUs', 'terms');
  if (contact?.heading) {
    const content = [
      contact.paragraph || '',
      contact.company || '',
      contact.address || '',
      contact.city || '',
      contact.country || '',
      contact.phone || '',
      contact.email || ''
    ].filter(Boolean).join('\n');
    sections.push(buildSection('contact', contact.heading, content));
  }

  return sections;
}

/**
 * Map Cookie Policy translations to sections schema.
 */
export function mapCookiePolicyToSections(t) {
  const sections = [];

  // Introduction
  const intro = t('introduction', 'cookies');
  if (intro?.heading) {
    const content = [
      intro.paragraph || '',
      intro.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('introduction', intro.heading, content));
  }

  // What are cookies?
  const whatAreCookies = t('whatAreCookies', 'cookies');
  if (whatAreCookies?.heading) {
    const content = [
      whatAreCookies.paragraph || '',
      whatAreCookies.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('what-are-cookies', whatAreCookies.heading, content));
  }

  // Why do we use cookies?
  const whyDoWeUseCookies = t('whyDoWeUseCookies', 'cookies');
  if (whyDoWeUseCookies?.heading) {
    sections.push(buildSection('why-do-we-use-cookies', whyDoWeUseCookies.heading, whyDoWeUseCookies.paragraph || ''));
  }

  // How can I control cookies?
  const howCanIControlCookies = t('howCanIControlCookies', 'cookies');
  if (howCanIControlCookies?.heading) {
    const content = [
      howCanIControlCookies.paragraph || '',
      howCanIControlCookies.paragraph2 || '',
      howCanIControlCookies.paragraph3 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('how-can-i-control-cookies', howCanIControlCookies.heading, content));
  }

  // How can I control cookies on my browser?
  const howCanIControlCookiesOnBrowser = t('howCanIControlCookiesOnBrowser', 'cookies');
  if (howCanIControlCookiesOnBrowser?.heading) {
    const contentParts = [howCanIControlCookiesOnBrowser.paragraph || ''];
    
    if (howCanIControlCookiesOnBrowser.browsers && Array.isArray(howCanIControlCookiesOnBrowser.browsers)) {
      contentParts.push(howCanIControlCookiesOnBrowser.browsers.map(browser => `- ${browser}`).join('\n'));
    }
    
    if (howCanIControlCookiesOnBrowser.paragraph2) {
      contentParts.push(howCanIControlCookiesOnBrowser.paragraph2);
    }
    
    if (howCanIControlCookiesOnBrowser.advertisingNetworks && Array.isArray(howCanIControlCookiesOnBrowser.advertisingNetworks)) {
      contentParts.push(howCanIControlCookiesOnBrowser.advertisingNetworks.map(network => `- ${network}`).join('\n'));
    }
    
    sections.push(buildSection('how-can-i-control-cookies-on-browser', howCanIControlCookiesOnBrowser.heading, contentParts.filter(Boolean).join('\n\n')));
  }

  // What about other tracking technologies?
  const otherTrackingTechnologies = t('otherTrackingTechnologies', 'cookies');
  if (otherTrackingTechnologies?.heading) {
    sections.push(buildSection('other-tracking-technologies', otherTrackingTechnologies.heading, otherTrackingTechnologies.paragraph || ''));
  }

  // Do you use Flash cookies?
  const flashCookies = t('flashCookies', 'cookies');
  if (flashCookies?.heading) {
    const content = [
      flashCookies.paragraph || '',
      flashCookies.paragraph2 || '',
      flashCookies.paragraph3 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('flash-cookies', flashCookies.heading, content));
  }

  // Do you serve targeted advertising?
  const targetedAdvertising = t('targetedAdvertising', 'cookies');
  if (targetedAdvertising?.heading) {
    sections.push(buildSection('targeted-advertising', targetedAdvertising.heading, targetedAdvertising.paragraph || ''));
  }

  // How often will you update this Cookie Policy?
  const policyUpdates = t('policyUpdates', 'cookies');
  if (policyUpdates?.heading) {
    const content = [
      policyUpdates.paragraph || '',
      policyUpdates.paragraph2 || ''
    ].filter(Boolean).join('\n\n');
    sections.push(buildSection('policy-updates', policyUpdates.heading, content));
  }

  // Where can I get further information?
  const furtherInformation = t('furtherInformation', 'cookies');
  if (furtherInformation?.heading) {
    const content = [
      furtherInformation.paragraph || '',
      furtherInformation.company || '',
      furtherInformation.address || '',
      furtherInformation.city || '',
      furtherInformation.phone || ''
    ].filter(Boolean).join('\n');
    sections.push(buildSection('further-information', furtherInformation.heading, content));
  }

  return sections;
}

/**
 * Map GDPR translations to sections schema.
 */
export function mapGdprToSections(t) {
  const sections = [];

  const commitment = t('privacyCommitment', 'gdpr');
  if (commitment?.heading) {
    sections.push(buildSection('commitment', commitment.heading, commitment.paragraph || ''));
  }

  const rights = t('gdprRights', 'gdpr');
  if (rights?.heading) {
    const content = Object.values(rights).filter(v => typeof v === 'object' && v !== null && v.title).map(right =>
      `**${right.title}**\n${right.description || ''}\n${right.action || ''} ${right.instruction || ''}`
    ).join('\n\n');
    sections.push(buildSection('rights', rights.heading, content));
  }

  const measures = t('technicalMeasures', 'gdpr');
  if (measures?.heading) {
    const content = Object.values(measures).filter(v => typeof v === 'object' && v !== null && v.title).map(measure =>
      `**${measure.title}**\n${measure.description || ''}`
    ).join('\n\n');
    sections.push(buildSection('technical-measures', measures.heading, content));
  }

  const legalBases = t('legalBases', 'gdpr');
  if (legalBases?.heading) {
    const content = Object.values(legalBases).filter(v => typeof v === 'object' && v !== null && v.title).map(basis =>
      `**${basis.title}**\n${basis.description || ''}`
    ).join('\n\n');
    sections.push(buildSection('legal-bases', legalBases.heading, content));
  }

  const dataProcessingRegister = t('dataProcessingRegister', 'gdpr');
  if (dataProcessingRegister?.heading) {
    sections.push(buildSection('data-processing-register', dataProcessingRegister.heading, dataProcessingRegister.description || ''));
  }

  const thirdParties = t('thirdParties', 'gdpr');
  if (thirdParties?.heading) {
    sections.push(buildSection('third-parties', thirdParties.heading, thirdParties.description || ''));
  }

  const dpo = t('dataProtectionOfficer', 'gdpr');
  if (dpo?.heading) {
    const content = [
      dpo.name || '',
      dpo.email || '',
      dpo.phone || ''
    ].filter(Boolean).join('\n');
    sections.push(buildSection('dpo', dpo.heading, content));
  }

  return sections;
}


